<template>
  <div class="ma-4">
    <v-row>
      <v-col class="xs12 col-md-4">
        <!-- filter -->
        <v-row class="no-gutters pb-3">
          <v-col>
            <v-card outlined>
              <v-card-title class="clickable" @click="cardOpen = !cardOpen">
                <v-row class="no-gutters justify-space-between">
                  <v-col class="col-auto">
                    {{ $t('v4.filter') }}
                  </v-col>
                  <v-col class="col-auto">
                    <v-btn icon>
                      <span v-if="cardOpen" class="fal fa-chevron-up fa-2x" />
                      <span v-else class="fal fa-chevron-down fa-2x" />
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-title>
              <v-expand-transition>
                <v-card-text v-if="cardOpen">
                  <v-select
                    v-model="selectedCancellationType"
                    :items="cancellationTypesInstance.getCancellationTypes()"
                    return-object
                    :label="$t('v4.selectCancellationType')"
                    item-text="description"
                    hide-details
                    outlined
                  />
                </v-card-text>
              </v-expand-transition>
            </v-card>
          </v-col>
        </v-row>

        <!-- exports cancellations on PC -->
        <storno-export />
        <voucher-export />
      </v-col>

      <!-- cancellations table -->
      <v-col class="xs12 col-md-8">
        <v-card outlined>
          <v-card-title>
            {{ $t('v4.stornoHistory') }}
          </v-card-title>
          <v-card-text>
            <v-data-table
              v-if="cancellationsInstance && cancellationsInstance.pagination"
              :headers="headers"
              :items="cancellationsInstance.getCancellations()"
              :no-data-text="$t('dataTable.noDataAvailable')"
              class="elevation-0"
              :page.sync="cancellationsInstance.pagination.page"
              :items-per-page="itemsPerPage"
              :loading="cancellationsInstance.loading"
              hide-default-footer
            >
              <template slot="item" slot-scope="props">
                <tr
                  class="clickable"
                  @click="openCancellationDetailModal(props.item)"
                >
                  <!-- cancellation date -->
                  <td>
                    {{ props.item.getCancellationDate() }}
                  </td>

                  <!-- cancellation user -->
                  <td>
                    {{ props.item.getUser().getFullName() }}
                  </td>

                  <!-- amount ration -->
                  <td>
                    {{ props.item.getCancellationRatio($options) }}
                  </td>

                  <!-- voucher holder -->
                  <td>
                    <span
                      v-if="
                        props.item.getVoucher() &&
                        props.item.getVoucher().getUser()
                      "
                    >
                      {{ props.item.getVoucher().getUser().getFullName() }}
                    </span>
                    <span v-else>
                      {{ $t('v4.noNameFound') }}
                    </span>
                  </td>

                  <!-- voucher already used? -->
                  <td class="text-center">
                    <!-- no voucher -->
                    <span v-if="!props.item.getVoucher()">
                      {{ $t('v4.noVoucherHasBeenGenerated') }}
                    </span>

                    <!-- voucher used -->
                    <span
                      v-else-if="!props.item.getVoucher().isValid()"
                      class="fa fa-check green--text"
                    ></span>

                    <!-- voucher not used -->
                    <span v-else class="fa fa-times primary--text"></span>
                  </td>
                </tr>
              </template>
              <!-- footer -->
              <template #footer>
                <table-footer
                  disable-page-count-display
                  :page-count="pageCount"
                  :itemsPerPage.sync="itemsPerPage"
                  :disable-next-arrow="disableNextArrow"
                  :page="page"
                  @nextPage="
                    () => {
                      page++
                    }
                  "
                  @previousPage="
                    () => {
                      page--
                    }
                  "
                >
                </table-footer>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- cancellation detail modal -->
    <s-modal
      ref="cancellationDetailModal"
      :size="1"
      :headerText="$t('v4.cancellationDetailModalHeader')"
      hide-ok-button
      :cancel-text="$t('general.close')"
    >
      <!-- cancellation details -->
      <v-layout v-if="selectedCancellation" class="wrap px-3">
        <!-- title -->
        <v-flex class="xs12 border-bottom">
          <div class="subheading">
            {{ $t('v4.cancellation') }}
          </div>
        </v-flex>

        <!-- cancellation details -->
        <v-flex class="xs12 font-weight-light">
          <v-layout v-if="selectedCancellation" class="wrap">
            <!-- cancellation user -->
            <v-flex class="xs6">
              <div>{{ $t('v4.selectCancellationType') }}:</div>
            </v-flex>
            <v-flex class="xs6">
              <div>
                {{ selectedCancellation.getUser().getFullName() }}
              </div>
            </v-flex>

            <!-- cancellation date -->
            <v-flex class="xs6">
              <div>{{ $t('general.date') }}:</div>
            </v-flex>
            <v-flex class="xs6">
              <div>
                {{ selectedCancellation.getCancellationDate() }}
              </div>
            </v-flex>

            <!-- cancellation value -->
            <v-flex class="xs6">
              <div>{{ $t('v4.voucherValue') }}:</div>
            </v-flex>
            <v-flex class="xs6">
              <div>
                <span v-if="selectedCancellation.getVoucher()">
                  {{ selectedCancellation.getVoucher().getValue() | currency }}
                </span>

                <!-- no voucher generated -->
                <span v-else>
                  {{ $t('v4.noVoucherHasBeenGenerated') }}
                </span>
              </div>
            </v-flex>

            <!-- original ticket price -->
            <v-flex class="xs6">
              <div>{{ $t('v4.paidOriginalTicketPrice') }}:</div>
            </v-flex>
            <v-flex class="xs6">
              <div>
                {{ selectedCancellation.getOriginalAmount() | currency }}
              </div>
            </v-flex>

            <!-- cancel reason -->
            <v-flex class="xs6">
              <div>{{ $t('v4.giveMeAStornoReason') }}:</div>
            </v-flex>
            <v-flex class="xs6">
              {{ selectedCancellation.getReason() }}
            </v-flex>
          </v-layout>
        </v-flex>

        <!-- ticket owner -->
        <v-flex
          v-if="selectedCancellation.getBookingEntries().length > 0"
          class="xs12 border-bottom mt-6"
        >
          <div class="subheading">
            {{ $t('v4.ticketOwner') }}
          </div>
        </v-flex>

        <!-- single booking entries -->
        <v-flex
          v-for="(
            booking, bookingIndex
          ) in selectedCancellation.getBookingEntries()"
          :key="booking.id"
          class="xs12 font-weight-light"
          :class="{
            'grey lighten-1': bookingIndex % 2 !== 0,
            'mb-4':
              bookingIndex <
              selectedCancellation.getBookingEntries().length - 1,
          }"
        >
          <v-layout class="wrap">
            <!-- name -->
            <v-flex class="xs6"> {{ $t('general.name') }}: </v-flex>
            <v-flex class="xs6">
              {{ booking.getUser().getFullName() }}
            </v-flex>

            <!-- address -->
            <v-flex class="xs6"> {{ $t('general.address') }}: </v-flex>
            <v-flex class="xs6">
              {{ booking.getUser().getAddress() }}
            </v-flex>

            <!-- zip city -->
            <v-flex class="xs6">
              {{ $t('general.zip') }} / {{ $t('general.city') }}:
            </v-flex>
            <v-flex class="xs6">
              {{ booking.getUser().getZip() }} {{ booking.getUser().getCity() }}
            </v-flex>

            <!-- country -->
            <v-flex class="xs6"> {{ $t('general.country') }}: </v-flex>
            <v-flex class="xs6">
              {{ booking.getUser().getCountry() }}
            </v-flex>

            <!-- mail -->
            <v-flex class="xs6"> {{ $t('general.email') }}: </v-flex>
            <v-flex class="xs6">
              {{ booking.getUser().getMail() }}
            </v-flex>
          </v-layout>
          <v-spacer v-if="selectedCancellation.getBookingEntries().length > 0">
          </v-spacer>
        </v-flex>

        <!-- voucher owner -->
        <v-flex class="xs12 border-bottom mt-6">
          <div class="subheading">
            {{ $t('v4.voucherOwner') }}
          </div>
        </v-flex>

        <!-- info -->
        <v-flex class="xs12 font-weight-light my-2">
          <span class="fa fa-info-circle blue--text mr-2"></span>
          {{ $t('v4.voucherOwnerInfo') }}
        </v-flex>

        <!-- details -->
        <v-layout
          v-if="selectedCancellation.getVoucher()"
          class="wrap font-weight-light"
        >
          <v-flex class="xs12 font-weight-light">
            <v-layout class="wrap">
              <!-- name -->
              <v-flex class="xs6"> {{ $t('general.name') }}: </v-flex>
              <v-flex class="xs6">
                {{ selectedCancellation.getVoucher().getUser().getFullName() }}
              </v-flex>

              <!-- address -->
              <v-flex class="xs6"> {{ $t('general.address') }}: </v-flex>
              <v-flex class="xs6">
                {{ selectedCancellation.getVoucher().getUser().getAddress() }}
              </v-flex>

              <!-- zip city -->
              <v-flex class="xs6">
                {{ $t('general.zip') }} / {{ $t('general.city') }}:
              </v-flex>

              <v-flex class="xs6">
                {{ selectedCancellation.getVoucher().getUser().getZip() }}
                {{ selectedCancellation.getVoucher().getUser().getCity() }}
              </v-flex>

              <!-- country -->
              <v-flex class="xs6"> {{ $t('general.country') }}: </v-flex>
              <v-flex class="xs6">
                {{ selectedCancellation.getVoucher().getUser().getCountry() }}
              </v-flex>

              <!-- mail -->
              <v-flex class="xs6"> {{ $t('general.email') }}: </v-flex>
              <v-flex class="xs6">
                {{ selectedCancellation.getVoucher().getUser().getMail() }}
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>

        <!-- no voucher generated -->
        <v-flex v-else class="xs12 font-weight-light">
          <span class="fa fa-info-circle blue--text mr-2"></span>
          {{ $t('v4.noVoucherGeneratedInfo') }}
        </v-flex>
      </v-layout>
    </s-modal>
  </div>
</template>

<script>
import CancellationTypes from '../../classes-shared/cancellation/CancellationTypes'
import Cancellations from '../../classes-shared/cancellation/Cancellations'
import TableFooter from '../../components-shared/utils/vuetifyHelper/TableFooter'
import StornoExport from '@/components-internal/stornoHistory/StornoExport'
import VoucherExport from '@/components-internal/stornoHistory/VoucherExport'

export default {
  name: 'StornoHistory',

  components: {
    VoucherExport,
    StornoExport,
    TableFooter,
  },

  data() {
    return {
      page: 1,
      pageCount: 1,
      itemsPerPage: 10,
      disableNextArrow: false,
      selectedCancellationType: null,

      // Type: Cancellations
      cancellationsInstance: null,

      selectedCancellation: null,

      dateRange: null,
      dateFormat: 'YYYY-MM-DDTHH:mm:ss',
      cardOpen: false,
    }
  },

  computed: {
    cancellationTypesInstance() {
      return new CancellationTypes()
    },

    // headers of table
    headers() {
      return [
        {
          text: this.$t('v4.cancellationDate'),
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('v4.selectCancellationType'),
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('v4.voucherOriginalRatio'),
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('v4.voucherOwner'),
          align: 'left',
          sortable: false,
        },
        {
          text: this.$t('v4.alreadyUsed'),
          align: 'center',
          sortable: false,
        },
      ]
    },
  },

  watch: {
    // load cancellations whenever cancellation type is changed
    async selectedCancellationType() {
      // new instance
      this.cancellationsInstance = new Cancellations(this.$store)

      // load cancellations from api
      await this.cancellationsInstance.loadCancellations(
        this.selectedCancellationType.getType(),
        this.page,
        this.itemsPerPage
      )
      this.updateAvailablePages()
    },
    // load cancellations whenever page is changed
    async page() {
      await this.cancellationsInstance.loadCancellations(
        this.selectedCancellationType.getType(),
        this.page,
        this.itemsPerPage
      )
      this.updateAvailablePages()
    },
    async itemsPerPage() {
      await this.cancellationsInstance.loadCancellations(
        this.selectedCancellationType.getType(),
        this.page,
        this.itemsPerPage
      )
      this.updateAvailablePages()
    },
  },

  mounted() {
    // set standard cancellation type => triggers the watcher => triggers to load cancellations
    this.selectedCancellationType = this.cancellationTypesInstance.getStandardCancellationType()
  },

  methods: {
    openCancellationDetailModal(selectedCancellation) {
      // select cancellation
      this.selectedCancellation = selectedCancellation

      // open modal
      this.$refs.cancellationDetailModal.show()
    },
    updateAvailablePages() {
      if (
        this.cancellationsInstance.getCancellations().length ===
        this.itemsPerPage
      ) {
        this.disableNextArrow = false
        this.pageCount += 1
      } else {
        this.disableNextArrow = true
      }
    },
  },
}
</script>
